import { useMemo, useState } from 'react';

import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { InlineLoader } from '@/components/common';
import type { DeploymentOption } from '@/types/schedule';

interface DeploymentSelectorProps {
  deployments: DeploymentOption[];
  value: DeploymentOption | null;
  onChange: (deployment: DeploymentOption | null) => void;
  isLoading?: boolean;
  error?: string;
  disabled?: boolean;
  filterEnvironmentId?: string;
}

export const DeploymentSelector = ({
  deployments,
  value,
  onChange,
  isLoading = false,
  error,
  disabled = false,
  filterEnvironmentId,
}: DeploymentSelectorProps) => {
  const [inputValue, setInputValue] = useState('');

  // Filter deployments by environment if specified
  const filteredDeployments = useMemo(() => {
    if (!filterEnvironmentId) return deployments;
    return deployments.filter((d) => String(d.environmentId) === String(filterEnvironmentId));
  }, [deployments, filterEnvironmentId]);

  // Sort deployments: by environment name, then by recipe name
  const sortedDeployments = useMemo(() => {
    return [...filteredDeployments].sort((a, b) => {
      const envCompare = a.environmentName.localeCompare(b.environmentName);
      if (envCompare !== 0) return envCompare;
      return a.transformerName.localeCompare(b.transformerName);
    });
  }, [filteredDeployments]);

  const handleChange = (_event: React.SyntheticEvent, newValue: DeploymentOption | null) => {
    onChange(newValue);
  };

  return (
    <Box>
      <Autocomplete
        value={value}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
        options={sortedDeployments}
        groupBy={(option) => option.environmentName}
        getOptionLabel={(option) => `${option.transformerName} (v${option.version})`}
        isOptionEqualToValue={(option, val) =>
          option.baseId === val.baseId && option.environmentId === val.environmentId
        }
        loading={isLoading}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Deployed Recipe"
            placeholder="Search recipes..."
            error={!!error}
            helperText={error || 'Select a recipe that has been deployed to an environment'}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <InlineLoader size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key: _key, ...otherProps } = props;
          return (
            <ListItem key={`${option.baseId}-${option.environmentId}`} {...otherProps}>
              <ListItemText
                primary={option.transformerName}
                secondary={`Version ${option.version}`}
              />
            </ListItem>
          );
        }}
        renderGroup={(params) => (
          <li key={params.key}>
            <Box
              sx={{
                position: 'sticky',
                top: -8,
                bgcolor: 'background.paper',
                px: 2,
                py: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" color="primary">
                {params.group}
              </Typography>
            </Box>
            <ul style={{ padding: 0 }}>{params.children}</ul>
          </li>
        )}
      />

      {/* Selected deployment summary */}
      {value && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary">
              Selected:
            </Typography>
            <Chip label={value.transformerName} size="small" color="primary" />
            <Chip label={`v${value.version}`} size="small" variant="outlined" />
            <Chip label={value.environmentName} size="small" color="secondary" />
          </Stack>
        </Box>
      )}
    </Box>
  );
};

// Simpler list-based selector for when you want to show all options
interface DeploymentListProps {
  deployments: DeploymentOption[];
  selectedId?: string;
  onSelect: (deployment: DeploymentOption) => void;
  isLoading?: boolean;
}

export const DeploymentList = ({
  deployments,
  selectedId,
  onSelect,
  isLoading = false,
}: DeploymentListProps) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (deployments.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No deployed recipes found. Deploy a recipe first to create a schedule.
        </Typography>
      </Box>
    );
  }

  // Group by environment
  const grouped = new Map<string, DeploymentOption[]>();
  deployments.forEach((d) => {
    const existing = grouped.get(d.environmentName) || [];
    grouped.set(d.environmentName, [...existing, d]);
  });

  return (
    <Stack spacing={2}>
      {Array.from(grouped.entries()).map(([envName, envDeployments]) => (
        <Box key={envName}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            {envName}
          </Typography>
          <Stack spacing={1}>
            {envDeployments.map((deployment) => {
              const isSelected = selectedId === `${deployment.baseId}-${deployment.environmentId}`;
              return (
                <Box
                  key={`${deployment.baseId}-${deployment.environmentId}`}
                  onClick={() => onSelect(deployment)}
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    cursor: 'pointer',
                    bgcolor: isSelected ? 'primary.lighter' : 'transparent',
                    '&:hover': {
                      bgcolor: isSelected ? 'primary.lighter' : 'action.hover',
                    },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{deployment.transformerName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ver. {deployment.version}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      {deployment.processingType && (
                        <Chip
                          label={deployment.processingType}
                          size="small"
                          sx={{
                            bgcolor: '#6D92B8',
                            color: '#fff',
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                          }}
                        />
                      )}
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};
